<script setup>
/**
 * Can this number use forms — the verdict, the reason, and the three signals it was read from.
 *
 * ── IT TURNS A CODE INTO A SENTENCE, AND NEVER THE OTHER WAY ROUND ─────────────────
 *
 * The server sends `{ eligible, code, signals }` and no prose. That is deliberate: a sentence
 * written on the server arrives in one language, and this product is bilingual. So every word
 * here comes out of `i18n.js`, and a code the message table has no sentence for falls back to
 * the "we have not heard from WhatsApp" wording rather than rendering blank — a blank refusal is
 * worse than no gate, because the owner cannot tell whether anything is wrong.
 *
 * ── THE SIGNALS ARE SHOWN EVEN WHEN THE VERDICT IS YES ─────────────────────────────
 *
 * They are what the answer was read from, and two of them are REPORTED AND NOT DECISIVE: the
 * messaging tier, because Meta documents no minimum tier for forms and refusing a number that
 * would have worked is worse than showing a low figure beside a good verdict; and the number's
 * standing, because the rule that acts on a suspended number lives with the other rules in the
 * form builder rather than in this file. Showing a signal is not the same as judging it, and this
 * component judges nothing.
 */
import { computed } from 'vue';

import { toIntlLocale } from '../helpers/intl.js';
import { useWasloI18n } from '../i18n.js';

const props = defineProps({
  eligibility: { type: Object, required: true },
});

const { t, locale } = useWasloI18n();

const isReady = computed(() => props.eligibility.eligible === true);

/**
 * A VERDICT THIS BUILD HAS NEVER SEEN IS EXPECTED, NOT A BUG. The rules live in the sidecar and
 * are still growing, so a new `code` will arrive before this table has a sentence for it — and
 * the server already refuses to call an unrecognised verdict eligible, so the only question here
 * is what to SAY.
 *
 * vue-i18n hands the key back when it has no entry. Rather than pick the nearest sentence we
 * happen to own — which would send an owner to fix the wrong thing — an unknown code renders the
 * server's own explanation, quoted. When the server did not explain either, it says that too.
 */
const reason = computed(() => {
  const key = `WASLO.FORMS.WHY.${String(props.eligibility.code ?? '').toUpperCase()}`;
  const sentence = t(key);
  if (sentence !== key) return sentence;
  return props.eligibility.reason
    ? t('WASLO.FORMS.WHY.UNTOLD', { reason: props.eligibility.reason })
    : t('WASLO.FORMS.WHY.UNTOLD_SILENT');
});

const signals = computed(() => {
  const read = props.eligibility.signals ?? {};
  return [
    {
      key: 'name',
      label: t('WASLO.FORMS.SIGNALS.NAME'),
      value: read.verifiedName || t('WASLO.FORMS.SIGNALS.NAME_UNTOLD'),
    },
    {
      key: 'rating',
      label: t('WASLO.FORMS.SIGNALS.RATING'),
      value: read.qualityRating || t('WASLO.FORMS.SIGNALS.RATING_UNTOLD'),
    },
    {
      // REPORTED, NOT DECISIVE, and deliberately still here. The rule that acts on a suspended
      // number lives with the others in the sidecar, not in this file and not in the fork — but
      // an owner whose number WhatsApp has restricted should be able to SEE that on the screen
      // that tells her whether forms work, rather than learning it from a failed send.
      key: 'standing',
      label: t('WASLO.FORMS.SIGNALS.STANDING'),
      value: read.status || t('WASLO.FORMS.SIGNALS.STANDING_UNTOLD'),
    },
    {
      key: 'tier',
      label: t('WASLO.FORMS.SIGNALS.TIER'),
      value: read.messagingLimitTier || t('WASLO.FORMS.SIGNALS.TIER_UNTOLD'),
    },
  ];
});

const checked = computed(() => {
  if (!props.eligibility.checkedAt) return '';
  const when = new Date(props.eligibility.checkedAt);
  if (Number.isNaN(when.getTime())) return '';
  return t('WASLO.FORMS.SIGNALS.CHECKED', {
    when: when.toLocaleString(toIntlLocale(locale.value), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }),
  });
});
</script>

<template>
  <div
    class="flex flex-col gap-4 px-6 py-5 outline outline-1 -outline-offset-1 rounded-xl outline-n-container"
    :class="isReady ? 'bg-n-solid-2' : 'bg-n-amber-2'"
    data-waslo-forms-eligibility
    :data-eligible="isReady ? 'yes' : 'no'"
    :data-code="eligibility.code"
  >
    <div class="flex items-start gap-4">
      <span
        class="grid shrink-0 place-items-center size-9 rounded-xl"
        :class="isReady ? 'bg-n-teal-3' : 'bg-n-amber-3'"
        aria-hidden="true"
      >
        <span
          class="size-5"
          :class="
            isReady
              ? 'i-lucide-badge-check text-n-teal-11'
              : 'i-lucide-alert-triangle text-n-amber-11'
          "
        />
      </span>
      <div class="flex flex-col gap-1">
        <p class="mb-0 text-sm font-medium text-n-slate-12">
          {{
            isReady
              ? t('WASLO.FORMS.READY.TITLE')
              : t('WASLO.FORMS.NOT_READY.TITLE')
          }}
        </p>
        <p
          class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
          data-waslo-forms-reason
        >
          {{ isReady ? t('WASLO.FORMS.READY.BODY') : reason }}
        </p>
      </div>
    </div>

    <dl class="grid gap-3 mb-0 sm:grid-cols-2 lg:grid-cols-4">
      <div
        v-for="signal in signals"
        :key="signal.key"
        class="flex flex-col gap-0.5"
        :data-waslo-forms-signal="signal.key"
      >
        <dt class="text-xs leading-5 text-n-slate-10">{{ signal.label }}</dt>
        <dd class="mb-0 text-sm font-medium break-words text-n-slate-12">
          {{ signal.value }}
        </dd>
      </div>
    </dl>

    <p
      v-if="checked"
      class="mb-0 text-xs leading-5 text-n-slate-10"
      data-waslo-forms-checked
    >
      {{ checked }}
    </p>
  </div>
</template>
